// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Control-plane dashboard.
 *
 * The screen renders the four counters `GET /control-plane/summary` publishes
 * and nothing more. `ADM-014` prohibits claiming readiness from a dashboard and
 * prohibits rendering missing telemetry as zero or healthy; both hold here for
 * the same reason, which is that nothing on this screen is invented. Every tile
 * is a count Platform took from its own stored collections, so there is no
 * unobserved metric to misreport and no health rating to imply — a count of
 * registered colleges says how many there are, not whether they are well.
 *
 * The screen deliberately does not display an observation timestamp or a
 * degraded-reason banner. Platform publishes neither, and a timestamp the
 * browser made up would be worse than none.
 */

import {
  AsyncSection,
  capabilities,
  PageHeader,
  queryKeys,
  RequireCapability,
  useSession,
} from '@eai/ui-shared';
import type { ControlPlaneSummary } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { useControlPlaneQuery } from '../app-shell';

/** One counter as the dashboard presents it. */
export interface SummaryCount {
  /** Stable identity, matching the contract member it came from. */
  readonly key: keyof ControlPlaneSummary;
  /** Operator-facing label for that member. */
  readonly label: string;
  /** The counted value, exactly as Platform reported it. */
  readonly value: number;
}

/**
 * Label the four published counters.
 *
 * A pure projection: one entry per contract member, in contract order, with no
 * derived, combined, or defaulted figure among them. Adding a fifth tile would
 * mean inventing a measurement Platform never took.
 *
 * @param summary - The validated summary response.
 * @returns One labelled count per member of the contract.
 */
export function summaryCounts(summary: ControlPlaneSummary): readonly SummaryCount[] {
  return [
    { key: 'tenants', label: 'Registered colleges', value: summary.tenants },
    { key: 'ai_services', label: 'AI services', value: summary.ai_services },
    { key: 'documents', label: 'Documents', value: summary.documents },
    { key: 'approvals_pending', label: 'Approvals pending', value: summary.approvals_pending },
  ];
}

/** Control-plane dashboard screen. */
export function DashboardScreen(): ReactNode {
  const { session } = useSession();
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.summary(scope),
    (client, signal) => client.getSummary(signal),
  );

  return (
    <RequireCapability required={[capabilities.tenantsRead]} area="Dashboard">
      <PageHeader
        title="Dashboard"
        description="Current platform posture for this scope."
        {...(session?.university_label === null || session?.university_label === undefined
          ? {}
          : { scopeLabel: session.university_label })}
      />
      {/* Every response carries all four counters, so there is no empty case to
          render: the counters are absent only when the request itself failed,
          which `AsyncSection` reports as an error. */}
      <AsyncSection
        query={query}
        loadingLabel="Loading platform summary"
        emptyTitle="No summary is available"
      >
        {(data) => (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              component="ul"
              sx={{ listStyle: 'none', m: 0, p: 0, flexWrap: 'wrap' }}
            >
              {summaryCounts(data).map((count) => (
                <Card
                  key={count.key}
                  component="li"
                  variant="outlined"
                  sx={{ minWidth: 220, flex: 1 }}
                  data-testid={`summary-count-${count.key}`}
                >
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {count.label}
                    </Typography>
                    <Typography variant="h2" component="p">
                      {new Intl.NumberFormat().format(count.value)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            <Alert severity="info" role="note" sx={{ mt: 2 }}>
              This dashboard reports counts held by the control plane. It is not a release,
              security, or operational readiness decision; those are recorded by their own review
              processes.
            </Alert>
          </>
        )}
      </AsyncSection>
    </RequireCapability>
  );
}
